import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TREBLE_LINES,
  BASS_LINES,
  STAFF_VIEWBOX,
  noteToY,
  ledgerLinesFor,
  MIDDLE_C_Y,
} from "@/lib/staffGeometry";
import { getDurationCategory } from "@/lib/duration";

const STAFF_LEFT = 70;     // left edge of staff lines
const STAFF_RIGHT_PAD = 30;
const NOTE_X_START = 140;
const NOTE_X_STEP = 36;    // horizontal step per letter
const WORD_GAP_EXTRA = 18; // additional gap between words
// Narrowest viewBox we'll ever render. The SVG always scales to fit its
// container, so a narrow viewBox = big notes (short words) and a wide
// viewBox = small notes (long phrases). Nothing ever scrolls.
const MIN_STAFF_WIDTH = 520;

// Key-signature engraving. When every occurrence of a letter carries the
// same accidental we hoist it next to the clefs like a real chart instead
// of repeating the glyph in front of each note.
const FLAT_ORDER = ["B", "E", "A", "D", "G", "C", "F"];
const SHARP_ORDER = ["F", "C", "G", "D", "A", "E", "B"];
const KEY_SIG_OCTAVES = {
  b: {
    treble: { B: 4, E: 5, A: 4, D: 5, G: 4, C: 5, F: 4 },
    bass: { B: 2, E: 3, A: 2, D: 3, G: 2, C: 3, F: 2 },
  },
  "#": {
    treble: { F: 5, C: 5, G: 5, D: 5, A: 4, E: 5, B: 4 },
    bass: { F: 3, C: 3, G: 3, D: 3, A: 2, E: 3, B: 2 },
  },
};
const KEY_SIG_START_X = STAFF_LEFT + 44;
const KEY_SIG_STEP_X = 12;

function computeKeySignature(entries) {
  const seen = new Map(); // note letter -> accidental | "mixed"
  entries.forEach((e) => {
    if (!e.note) return;
    const acc = e.accidental || null;
    if (!seen.has(e.note)) seen.set(e.note, acc);
    else if (seen.get(e.note) !== acc) seen.set(e.note, "mixed");
  });
  const flats = FLAT_ORDER.filter((n) => seen.get(n) === "b");
  const sharps = SHARP_ORDER.filter((n) => seen.get(n) === "#");
  // Mixed sharps + flats isn't a real key signature — keep those inline.
  if (flats.length && sharps.length) return { kind: null, names: [], letters: new Set(), endX: 0 };
  const kind = flats.length ? "b" : sharps.length ? "#" : null;
  const names = flats.length ? flats : sharps;
  return {
    kind,
    names,
    letters: new Set(names),
    endX: names.length ? KEY_SIG_START_X + names.length * KEY_SIG_STEP_X : 0,
  };
}

// Compute x positions and bar-line positions given drop entries.
function computeLayout(entries, startX = NOTE_X_START) {
  const xs = [];
  const bars = [];
  let x = startX;
  // Use displayWordIdx (the original logical word) for word-grouping. For
  // plain (non-remix) entries it's undefined, so fall back to wordIdx.
  const wordKey = (e) => (e.displayWordIdx !== undefined ? e.displayWordIdx : e.wordIdx);
  let prevWord = entries.length > 0 ? wordKey(entries[0]) : -1;
  entries.forEach((e, i) => {
    const w = wordKey(e);
    if (i > 0 && w !== prevWord) {
      bars.push({ x: x - NOTE_X_STEP / 2, afterIdx: i - 1 });
      x += WORD_GAP_EXTRA;
    }
    xs.push(x);
    x += NOTE_X_STEP;
    prevWord = w;
  });
  return { xs, bars, totalWidth: x };
}

// Measured mode: every step (note or rest) is an eighth, eight to a bar, and a
// real bar line is drawn on each bar boundary.
const MEASURED_BAR_GAP = 14;
const EIGHTHS_PER_BAR = 8;

function computeMeasuredLayout(steps, startX = NOTE_X_START) {
  const stepXs = [];
  const barLines = [];
  let x = startX;
  steps.forEach((s, i) => {
    const beat = s.beat ?? i % EIGHTHS_PER_BAR;
    if (i > 0 && beat === 0) {
      barLines.push({ x: x + (MEASURED_BAR_GAP - NOTE_X_STEP) / 2, afterStepIdx: i - 1 });
      x += MEASURED_BAR_GAP;
    }
    stepXs.push(x);
    x += NOTE_X_STEP;
  });
  return { stepXs, barLines, totalWidth: x };
}

// Rest glyphs, drawn rather than typeset (the Unicode music font isn't
// installed everywhere). `eighths` is 1, 2, 4 or 8.
const REST_COLOR = "#F2D589";
function RestGlyph({ x, eighths }) {
  if (eighths >= 8) {
    // Whole rest: hangs under the second line from the top.
    const y = TREBLE_LINES[1];
    return <rect x={x - 9} y={y} width="18" height="6" fill={REST_COLOR} />;
  }
  if (eighths >= 4) {
    // Half rest: sits on the middle line.
    const y = TREBLE_LINES[2];
    return <rect x={x - 9} y={y - 6} width="18" height="6" fill={REST_COLOR} />;
  }
  const midY = (TREBLE_LINES[1] + TREBLE_LINES[3]) / 2;
  if (eighths >= 2) {
    // Quarter rest: the zigzag.
    return (
      <path
        d={`M ${x - 3.5} ${midY - 11} L ${x + 3} ${midY - 4} L ${x - 3} ${midY + 1.5} L ${x + 3.5} ${midY + 7} Q ${x - 3} ${midY + 6} ${x + 1} ${midY + 12}`}
        stroke={REST_COLOR}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    );
  }
  // Eighth rest: flag dot plus a leaning stem.
  return (
    <>
      <circle cx={x - 3.4} cy={midY - 6} r="3.4" fill={REST_COLOR} />
      <path
        d={`M ${x - 1.2} ${midY - 7.6} Q ${x + 4.6} ${midY - 1} ${x + 2.2} ${midY + 8.5}`}
        stroke={REST_COLOR}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </>
  );
}

export default function Staff({
  droppedEntries,
  activeIndex,
  previewingWordIdx = -1,
  previewingKey = null,
  fluid = false,
  maxHeight = "40vh",
  // Pitch labels under each note. Handy in the studio, but they add
  // ~46px of vertical space, so the projector/live surfaces drop them.
  showNoteNames = !fluid,
  // Full sequence the caller is about to reveal. When provided, the staff
  // sizes itself (and fixes every note's x position) for the WHOLE
  // sequence up front, so notes drop into place one by one instead of the
  // staff rescaling on every step.
  reserveEntries = null,
  // Roughly one beat at the caller's playback tempo — drives which notation
  // glyph each note gets. Defaults to the live/projector feel.
  baseStepMs = 200,
  // Fixes the notation instead of deriving it from playback ms:
  //   "meditative" → every note a quarter
  //   "groove"     → eighths, with a quarter closing each word
  noteStyle = null,
  // Measured mode: the full eighth-note grid from measureSequence() — notes
  // and rests, each carrying { bar, beat }. When given, the staff engraves bar
  // lines and rests instead of the loose word spacing.
  measuredSteps = null,
}) {
  const { width, height } = STAFF_VIEWBOX;
  const isMeasured = Array.isArray(measuredSteps) && measuredSteps.length > 0;
  const layoutEntries = isMeasured
    ? measuredSteps.filter((s) => !s.rest)
    : reserveEntries && reserveEntries.length > droppedEntries.length
    ? reserveEntries
    : droppedEntries;
  const keySig = computeKeySignature(layoutEntries);
  const noteStartX = Math.max(NOTE_X_START, keySig.endX ? keySig.endX + 16 : 0);
  const loose = computeLayout(layoutEntries, noteStartX);
  const measuredLayout = isMeasured
    ? computeMeasuredLayout(measuredSteps, noteStartX)
    : null;
  // Step indexes of the sounding notes, in the order they drop in.
  const noteStepIdx = isMeasured
    ? measuredSteps.reduce((acc, s, i) => {
        if (!s.rest) acc.push(i);
        return acc;
      }, [])
    : null;
  const xs = isMeasured ? noteStepIdx.map((i) => measuredLayout.stepXs[i]) : loose.xs;
  const totalWidth = isMeasured ? measuredLayout.totalWidth : loose.totalWidth;
  const bars = isMeasured ? [] : loose.bars.filter((b) => b.afterIdx < droppedEntries.length);
  // Rests and bar lines only show once playback has passed them.
  const lastVisibleStep = !isMeasured
    ? -1
    : droppedEntries.length >= noteStepIdx.length
    ? measuredSteps.length - 1
    : droppedEntries.length
    ? noteStepIdx[droppedEntries.length - 1]
    : -1;
  const dynamicWidth = layoutEntries.length
    ? Math.max(MIN_STAFF_WIDTH, totalWidth + STAFF_RIGHT_PAD)
    : width;

  // Auto-scroll the horizontal container to keep the active note in view.
  const scrollerRef = useRef(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || activeIndex < 0 || activeIndex >= xs.length) return;
    const noteX = xs[activeIndex];
    // SVG renders at full dynamicWidth; the scroller crops it to its visible width.
    const visibleWidth = el.clientWidth;
    const renderedScale = el.scrollWidth / dynamicWidth || 1;
    const noteScrollX = noteX * renderedScale;
    const margin = Math.max(60, visibleWidth * 0.15);
    const minVisible = el.scrollLeft + margin;
    const maxVisible = el.scrollLeft + visibleWidth - margin;
    if (noteScrollX < minVisible || noteScrollX > maxVisible) {
      el.scrollTo({
        left: Math.max(0, noteScrollX - visibleWidth / 2),
        behavior: "smooth",
      });
    }
  }, [activeIndex, xs, dynamicWidth]);

  // Notation palette (brief §5.2): the staff is tekhelet — the sound, the
  // living thing that moves. The notes and the Hebrew letters above them are
  // gold — the letter, the written, the fixed thing.
  const staffStrokeColor = "#1E5A6B";
  const clefColor = "#FCA07A";
  const staffLeftBraceX = STAFF_LEFT;
  const staffEndX = dynamicWidth - STAFF_RIGHT_PAD;
  // Dropping the pitch labels frees the dead space at the bottom of the
  // drawing (kept generous enough for the lowest bass ledger notes). The
  // top gets headroom for the letter annotations above each note.
  // Headroom above only needs to clear a letter sitting over a stem-down
  // note; below needs room for the low bass letters (א) and, in the
  // studio, the pitch labels under those.
  const vbTop = -17;
  // Without the pitch labels there's ~34px of dead space under the bass staff.
  const vbHeight = height + (showNoteNames ? 20 : -14) - vbTop;
  // In fluid mode the staff keeps its aspect ratio and is capped at
  // `maxHeight`, so the cream card hugs the notation instead of
  // letterboxing a small staff inside a huge panel.
  const fluidWrap = fluid
    ? {
        maxWidth: `calc(${maxHeight} * ${(dynamicWidth / vbHeight).toFixed(3)})`,
        marginInline: "auto",
        boxShadow: "inset 0 0 0 1px #1A0F3C, 0 30px 80px rgba(0,0,0,0.55)",
      }
    : null;

  return (
    <div
      ref={scrollerRef}
      data-testid="grand-staff"
      className="w-full overflow-x-auto rounded-md"
      style={{
        background:
          "radial-gradient(ellipse at top, #0C0826 0%, #080419 100%)",
        boxShadow: "inset 0 0 0 1px #1A0F3C, 0 1px 0 rgba(0,0,0,0.2)",
        ...fluidWrap,
      }}
    >
      <svg
        viewBox={`0 ${vbTop} ${dynamicWidth} ${vbHeight}`}
        {...(fluid
          ? { width: "100%" }
          : { width: dynamicWidth, height: vbHeight })}
        preserveAspectRatio={fluid ? "xMidYMid meet" : "xMinYMid meet"}
        style={{
          // Outside fluid mode the notation renders 1:1 and the container
          // scrolls, so a long phrase keeps the same big letters as a short
          // one instead of being squeezed down to fit. The auto-scroll above
          // keeps the sounding note in view. Fluid mode (the projector) still
          // scales the whole staff to fit its screen.
          minWidth: fluid ? 0 : dynamicWidth,
          display: "block",
          // In fluid mode we let the SVG intrinsic ratio (from viewBox +
          // width=100%) drive the height, so the whole staff scales up
          // proportionally on a projector or 4K screen. A `maxHeight`
          // cap keeps it from blowing past the viewport on very-wide
          // aspect displays (ultrawide monitors, cropped browser).
          ...(fluid ? { height: "auto", maxHeight } : null),
        }}
        aria-label="Grand staff with dropped letters"
      >
        {/* paper grain overlay */}
        <defs>
          <pattern id="paperNoise" x="0" y="0" width="160" height="160" patternUnits="userSpaceOnUse">
            <rect width="160" height="160" fill="transparent" />
          </pattern>
        </defs>

        {/* Treble staff lines */}
        {TREBLE_LINES.map((y, i) => (
          <line
            key={`tl-${i}`}
            x1={STAFF_LEFT}
            x2={staffEndX}
            y1={y}
            y2={y}
            stroke={staffStrokeColor}
            strokeWidth="1"
          />
        ))}
        {/* Bass staff lines */}
        {BASS_LINES.map((y, i) => (
          <line
            key={`bl-${i}`}
            x1={STAFF_LEFT}
            x2={staffEndX}
            y1={y}
            y2={y}
            stroke={staffStrokeColor}
            strokeWidth="1"
          />
        ))}

        {/* Left brace: connecting vertical line + thicker bar */}
        <line
          x1={STAFF_LEFT}
          x2={STAFF_LEFT}
          y1={TREBLE_LINES[0]}
          y2={BASS_LINES[4]}
          stroke={staffStrokeColor}
          strokeWidth="1.5"
        />
        <rect
          x={STAFF_LEFT - 5}
          y={TREBLE_LINES[0]}
          width="3"
          height={BASS_LINES[4] - TREBLE_LINES[0]}
          fill={staffStrokeColor}
        />

        {/* Right end bar */}
        <line
          x1={staffEndX}
          x2={staffEndX}
          y1={TREBLE_LINES[0]}
          y2={TREBLE_LINES[4]}
          stroke={staffStrokeColor}
          strokeWidth="1"
        />
        <line
          x1={staffEndX}
          x2={staffEndX}
          y1={BASS_LINES[0]}
          y2={BASS_LINES[4]}
          stroke={staffStrokeColor}
          strokeWidth="1"
        />

        {/* Treble clef glyph, vertically centred on the treble staff */}
        <text
          x={staffLeftBraceX + 8}
          y={(TREBLE_LINES[0] + TREBLE_LINES[4]) / 2}
          fontSize="58"
          fill={clefColor}
          dominantBaseline="central"
          style={{ fontFamily: "'Noto Music','Bravura',serif" }}
        >
          {"\u{1D11E}"}
        </text>
        {/* Bass clef glyph, vertically centred on the bass staff */}
        <text
          x={staffLeftBraceX + 8}
          y={(BASS_LINES[0] + BASS_LINES[4]) / 2}
          fontSize="44"
          fill={clefColor}
          dominantBaseline="central"
          style={{ fontFamily: "'Noto Music','Bravura',serif" }}
        >
          {"\u{1D122}"}
        </text>

        {/* Key signature — hoisted accidentals, drawn on both staves */}
        {keySig.names.map((n, i) => (
          <g key={`ks-${n}`}>
            <text
              x={KEY_SIG_START_X + i * KEY_SIG_STEP_X}
              y={noteToY(n, KEY_SIG_OCTAVES[keySig.kind].treble[n])}
              textAnchor="middle"
              fontSize="22"
              fill={clefColor}
              dominantBaseline="central"
              opacity="0.85"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {keySig.kind === "#" ? "♯" : "♭"}
            </text>
            <text
              x={KEY_SIG_START_X + i * KEY_SIG_STEP_X}
              y={noteToY(n, KEY_SIG_OCTAVES[keySig.kind].bass[n])}
              textAnchor="middle"
              fontSize="22"
              fill={clefColor}
              dominantBaseline="central"
              opacity="0.85"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {keySig.kind === "#" ? "♯" : "♭"}
            </text>
          </g>
        ))}

        {/* Measured mode: real bar lines every eight eighths */}
        {isMeasured &&
          measuredLayout.barLines
            .filter((b) => b.afterStepIdx <= lastVisibleStep)
            .map((b, i) => (
              <line
                key={`mbar-${i}`}
                data-testid={`staff-bar-line-${i}`}
                x1={b.x}
                x2={b.x}
                y1={TREBLE_LINES[0]}
                y2={BASS_LINES[4]}
                stroke={staffStrokeColor}
                strokeWidth="1.4"
                opacity="0.9"
              />
            ))}

        {/* Measured mode: rests filling out each bar. Drawn, not font glyphs —
            the Unicode music symbols tofu on many systems. Consecutive eighths
            are merged upstream into quarter / half / whole rests. */}
        {isMeasured &&
          measuredSteps.map((step, i) => {
            if (!step.rest || step.restStart === false) return null;
            if (i > lastVisibleStep) return null;
            const span = step.restEighths || 1;
            const lastIdx = Math.min(i + span - 1, measuredSteps.length - 1);
            const rx =
              (measuredLayout.stepXs[i] + measuredLayout.stepXs[lastIdx]) / 2;
            return (
              <motion.g
                key={`rest-${i}`}
                data-testid={`staff-rest-${i}`}
                data-rest-eighths={span}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.72 }}
                transition={{ duration: 0.25 }}
              >
                <RestGlyph x={rx} eighths={span} />
              </motion.g>
            );
          })}

        {/* Word bar-lines */}
        {bars.map((bar, i) => (
          <line
            key={`bar-${i}`}
            x1={bar.x}
            x2={bar.x}
            y1={TREBLE_LINES[0]}
            y2={BASS_LINES[4]}
            stroke={staffStrokeColor}
            strokeWidth="1"
            strokeDasharray="2,3"
            opacity="0.62"
          />
        ))}

        {/* Letters / notes */}
        <AnimatePresence>
          {droppedEntries.map((entry, i) => {
            const x = xs[i];
            const y = noteToY(entry.note, entry.octave);
            const ledger = ledgerLinesFor(entry.note, entry.octave);
            const entryKey = `${entry.wordIdx}:${entry.charIdx}`;
            const isActivePreview = entryKey === previewingKey;
            const isInPreviewingWord =
              previewingWordIdx !== -1 && entry.wordIdx === previewingWordIdx;
            const isActive = i === activeIndex || isActivePreview;
            const nextEntry = layoutEntries[i + 1] || droppedEntries[i + 1];
            const endsWord = !nextEntry || nextEntry.wordIdx !== entry.wordIdx;
            const category =
              isMeasured
                ? "eighth"
                : noteStyle === "meditative"
                ? "quarter"
                : noteStyle === "groove"
                ? (endsWord ? "quarter" : "eighth")
                : getDurationCategory(entry.stepMs, baseStepMs);
            const isOpenHead = category === "whole" || category === "half";
            const hasStem = category !== "whole";
            const hasFlag = category === "eighth";

            // Stem direction: high notes (above middle C) get stem-down; lower get stem-up.
            const stemDown = y < MIDDLE_C_Y;
            const stemX = stemDown ? x - 7.5 : x + 7.5;
            const stemY1 = stemDown ? 2 : -2;
            const stemY2 = stemDown ? 28 : -28;

            // Notes in the previewing word get a soft red wash; the currently
            // sounding one pops to full red.
            const headFill = isOpenHead
              ? "transparent"
              : isActive
              ? "#F2D589"
              : isInPreviewingWord
              ? "#D3A968"
              : "#F2D589";
            const headStroke = isActive
              ? "#F2D589"
              : isInPreviewingWord
              ? "#D3A968"
              : "#F2D589";
            const headOpacity = isOpenHead
              ? 0.85
              : isActive
              ? 1
              : isInPreviewingWord
              ? 0.95
              : 0.82;

            return (
              <motion.g
                key={`note-${i}-${entry.wordIdx}-${entry.charIdx}-${entry.letter}`}
                data-note-index={i}
                data-testid={`staff-note-${i}`}
                initial={{ opacity: 0, y: y - 70 }}
                animate={{ opacity: 1, y }}
                exit={{ opacity: 0 }}
                transition={{ type: "spring", stiffness: 240, damping: 18 }}
              >
                {/* ledger lines */}
                {ledger.map((ly, li) => (
                  <line
                    key={`led-${li}`}
                    x1={x - 12}
                    x2={x + 12}
                    y1={ly - y}
                    y2={ly - y}
                    stroke={staffStrokeColor}
                    strokeWidth="1"
                  />
                ))}

                {/* Stem */}
                {hasStem && (
                  <line
                    x1={stemX}
                    y1={stemY1}
                    x2={stemX}
                    y2={stemY2}
                    stroke={headStroke}
                    strokeWidth="1.4"
                    opacity={isActive ? 0.95 : 0.7}
                  />
                )}

                {/* Eighth flag */}
                {hasFlag && (
                  <path
                    d={
                      stemDown
                        ? `M ${stemX} ${stemY2} q -8 -3 -10 -12`
                        : `M ${stemX} ${stemY2} q 8 3 10 12`
                    }
                    stroke={headStroke}
                    strokeWidth="1.4"
                    fill="none"
                    opacity={isActive ? 0.95 : 0.7}
                  />
                )}

                {/* Note head, filled for quarter/eighth, open for half/whole.
                    Whole notes drawn slightly wider and upright as per convention. */}
                <ellipse
                  cx={x}
                  cy={0}
                  rx={category === "whole" ? 9.5 : 8.5}
                  ry="6.2"
                  transform={`rotate(${category === "whole" ? 0 : -22} ${x} 0)`}
                  fill={headFill}
                  stroke={isOpenHead ? headStroke : "none"}
                  strokeWidth={isOpenHead ? 1.8 : 0}
                  opacity={headOpacity}
                />

                {/* Accidental glyph — only when it isn't already covered
                    by the key signature next to the clefs. */}
                {entry.accidental && !keySig.letters.has(entry.note) && (
                  <text
                    x={x - 15}
                    y={5}
                    textAnchor="end"
                    fontSize="16"
                    fill={isActive ? "#F2D589" : "#F2D589"}
                    opacity={isActive ? 1 : 0.7}
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {entry.accidental === "#" ? "♯" : "♭"}
                  </text>
                )}

                {/* Letter annotation — opposite side from the stem/flag:
                    above the head when the stem hangs down, below it when
                    the stem points up. */}
                <text
                  x={x}
                  y={stemDown ? -14 : 22}
                  textAnchor="middle"
                  style={{
                    fontFamily: "'Frank Ruhl Libre', serif",
                    fontWeight: 500,
                  }}
                  fontSize="23"
                  fill={isActive || isInPreviewingWord ? "#FFF7DC" : "#F2D589"}
                  opacity={isActive ? 1 : isInPreviewingWord ? 0.95 : 0.78}
                >
                  {entry.letter}
                </text>

                {/* pitch label below for clarity */}
                {showNoteNames ? (
                <text
                  x={x}
                  y={BASS_LINES[4] + 44 - y}
                  textAnchor="middle"
                  style={{ fontFamily: "'Courier Prime', monospace" }}
                  fontSize="10"
                  fill="#C6BBA9"
                  opacity="0.65"
                >
                  {entry.pitch}
                </text>
                ) : null}

                {/* active pulse */}
                {isActive && (
                  <motion.circle
                    cx={x}
                    cy={0}
                    r={14}
                    fill="none"
                    stroke="#FCA07A"
                    strokeWidth="1.5"
                    initial={{ opacity: 0.8, scale: 0.6 }}
                    animate={{ opacity: 0, scale: 2.4 }}
                    transition={{ duration: 0.7 }}
                  />
                )}
              </motion.g>
            );
          })}
        </AnimatePresence>

        <rect width="100%" height="100%" fill="url(#paperNoise)" />
      </svg>
    </div>
  );
}
